import { IntlMessageFormat } from 'intl-messageformat';

import { logger } from '@/utils/logger/logger';

const logTag = '[i18n:legacy-pseudo]';

/** In-memory pseudolocalised legacy `common` tree for the en-psaccent locale. */
let cachedPseudoCommon: unknown;

/** Clears the legacy pseudoloc cache between Jest cases. */
export function resetPseudolocalisedLegacyCommonJsonCacheForTests(): void {
  cachedPseudoCommon = undefined;
}

/**
 * Legacy i18next JSON does not use ICU. Only strings that look like plain literals
 * are pseudolocalised; anything with placeholders, markup, or `$t()` is left as-is.
 */
export function isPlainLiteralLegacyI18nString(value: string): boolean {
  if (value.length === 0) {
    return false;
  }
  if (/[{}<>]/.test(value)) {
    return false;
  }
  if (/\$t\s*\(/i.test(value)) {
    return false;
  }
  if (/%[sdiflot%]/i.test(value)) {
    return false;
  }
  if (/^https?:\/\//i.test(value.trim())) {
    return false;
  }
  return true;
}

async function pseudolocalisePlainLiteralString(value: string): Promise<string> {
  const [{ generateENXD }, { parse }] = await Promise.all([
    import('pseudolocalise'),
    import('@formatjs/icu-messageformat-parser'),
  ]);
  const ast = generateENXD(parse(value));
  const formatted = new IntlMessageFormat(ast, 'en').format();
  if (typeof formatted !== 'string') {
    return value;
  }
  return formatted;
}

async function walkJsonTree(node: unknown): Promise<unknown> {
  if (typeof node === 'string') {
    if (!isPlainLiteralLegacyI18nString(node)) {
      return node;
    }
    try {
      return await pseudolocalisePlainLiteralString(node);
    } catch {
      logger.debug(logTag, { event: 'leaf_skip', reason: 'pseudolocalise_failed' });
      return node;
    }
  }
  if (Array.isArray(node)) {
    return Promise.all(node.map((item) => walkJsonTree(item)));
  }
  if (node !== null && typeof node === 'object') {
    const entries = Object.entries(node as Record<string, unknown>);
    const out: Record<string, unknown> = {};
    await Promise.all(
      entries.map(async ([key, child]) => {
        out[key] = await walkJsonTree(child);
      })
    );
    return out;
  }
  return node;
}

/**
 * Returns a deep clone of `bundle` with plain literal string leaves pseudolocalised
 * (en-XD style). Non-literal strings are unchanged. Result is cached for
 * {@link SupportedLocale.EnPs} legacy `common` payloads.
 */
export async function applyPseudolocalisationToLegacyCommonJson(
  bundle: unknown
): Promise<unknown> {
  if (cachedPseudoCommon !== undefined) {
    return cachedPseudoCommon;
  }
  logger.debug(logTag, { event: 'build_start' });
  const built = await walkJsonTree(bundle);
  cachedPseudoCommon = built;
  logger.debug(logTag, { event: 'build_done' });
  return built;
}
